import * as Sentry from '@sentry/react';
import { FetchResponse } from 'ofetch';

import { ApiErrorRes } from '@/api/api-error.types';

const sanitizeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname;
  } catch {
    return url.split('?')[0];
  }
};

const STATUS_CODE_BLACKLIST = [401, 403, 409, 429];

const getEndpointName = (url: string): string => {
  const path = sanitizeUrl(url);
  const segments = path.split('/').filter(Boolean);
  // Remove version prefix (v1, v2, etc.) and join remaining segments
  const meaningfulSegments = segments.filter((s) => !/^v\d+$/.test(s));
  return meaningfulSegments.slice(-2).join('/') || 'unknown';
};

export const sentryCaptureApiError = (data: {
  request: RequestInfo;
  response?: FetchResponse<ApiErrorRes>;
  error?: Error;
  method?: string;
}) => {
  const { request, response, error } = data;
  const status = response?.status;

  if (status && STATUS_CODE_BLACKLIST.includes(status)) return;

  const method = data.method?.toUpperCase() || 'UNKNOWN';
  const correlationId = response?.headers?.get('x-correlation-id');

  const url = typeof request === 'string' ? request : request.url;
  const endpoint = getEndpointName(url);
  const errorCode = response?._data?.code;
  const errorMessage = response?._data?.message || response?._data?.data?.details;

  // Create descriptive error message for better Sentry grouping
  const sentryMessage = [
    `API ${status}`,
    method,
    endpoint,
    errorCode && `[${errorCode}]`,
    errorMessage,
  ]
    .filter(Boolean)
    .join(' · ');

  // Throw a formatted error so ofetch propagates it instead of the raw FetchError.
  // GlitchTip (and Sentry) capture it via the normal unhandledRejection path with the
  // descriptive message rather than the opaque "FetchError [POST] url: 400" form.
  // Attach response._data so catch blocks can still call getApiErrorMessage(e as TFetchError).
  const formattedError = new Error(sentryMessage) as Error & { data?: ApiErrorRes };
  formattedError.name = 'ApiError';
  formattedError.data = response?._data;

  Sentry.withScope((scope) => {
    scope.setFingerprint(['api_error', String(status), method, endpoint, errorCode || 'no_code']);
    scope.setTags({
      type: 'api_error',
      status_code: status?.toString(),
      http_method: method,
      endpoint,
      error_code: errorCode,
    });
    scope.setExtras({
      url,
      status,
      error_message: errorMessage,
      error_details: response?._data?.data?.details,
      correlation_id: correlationId,
      original_error: error?.message,
      original_stack: error?.stack,
    });
    Sentry.captureException(formattedError);
  });

  throw formattedError;
};
